import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Stack,
  useTheme,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  AppBar,
  Toolbar,
  Container,
  useMediaQuery
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { ExportOutlined, SwapOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setCurrentURL, updateAllCompetitors, restoreDefaultParameters } from 'store/reducers/actions';
import config from 'config';
import CompetitorCompareModal from 'sections/premiumBlock/CompetitorCompareModal';

// Favicon component to display company logo
const Favicon = ({ domain }) => {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  // Try to get larger favicon for better quality
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

  useEffect(() => {
    // Reset states when domain changes
    setError(false);
    setIsLoading(true);
  }, [domain]);

  return error ? (
    <BusinessIcon fontSize="medium" sx={{ color: theme.palette.grey[500] }} />
  ) : (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {isLoading && <BusinessIcon sx={{ fontSize: 24, color: 'text.disabled', position: 'absolute' }} />}
      <img
        src={faviconUrl}
        alt={domain.replace(/^https?:\/\//, '')}
        style={{
          width: 24,
          height: 24,
          objectFit: 'contain',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out'
        }}
        onError={() => setError(true)}
        onLoad={() => setIsLoading(false)}
      />
    </Box>
  );
};

const CompetitorCard = ({ competitor, onAnalyze, onSelect, isSelected }) => {
  const theme = useTheme();

  const handleURLClick = (link) => {
    window.open(link, '_blank');
  };

  return (
    <Card
      onClick={() => onSelect(competitor)}
      sx={{
        borderRadius: 2,
        border: isSelected ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
        bgcolor: isSelected ? `${theme.palette.primary.light}20` : 'background.paper',
        transition: 'all 0.2s ease-in-out',
        height: '100%',
        boxShadow: 'none',
        cursor: 'pointer',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          bgcolor: isSelected ? `${theme.palette.primary.light}20` : `${theme.palette.primary.light}05`
        }
      }}
    >
      <CardContent sx={{ py: 2, px: 2, height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%'
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
              <Box sx={{ flex: '1 1 auto', display: 'flex' }}>
                <Box
                  sx={{
                    borderRadius: '50%',
                    bgcolor: theme.palette.grey[100],
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                    alignSelf: 'center'
                  }}
                >
                  <Favicon domain={competitor.url} />
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold' }}>
                      {competitor.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: competitor.isEmerging ? 'info.lighter' : 'success.lighter',
                        color: competitor.isEmerging ? 'info.dark' : 'success.dark',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem'
                      }}
                    >
                      {competitor.isEmerging ? 'Emerging' : 'Established'}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {competitor.url.replace(/^https?:\/\//, '')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, flexShrink: 0 }}>
                <Tooltip title={`Analyze ${competitor.title}`} arrow placement="top">
                  <Button
                    size="small"
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAnalyze(competitor.url);
                    }}
                    sx={{ mr: 1, minWidth: 'auto', px: 1 }}
                  >
                    Analyze
                  </Button>
                </Tooltip>
                <Tooltip title="Validity Score shows how confident Askpot is that it is your competitor" arrow placement="top">
                  <Box sx={{ position: 'relative', display: 'inline-flex', mr: 1 }}>
                    <CircularProgress variant="determinate" value={100} size={32} sx={{ color: theme.palette.grey[200] }} />
                    <CircularProgress
                      variant="determinate"
                      value={competitor.validity_score * 100}
                      size={32}
                      sx={{
                        color: theme.palette.primary.light,
                        position: 'absolute',
                        left: 0,
                        circle: { strokeLinecap: 'round' }
                      }}
                    />
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '0.65rem',
                        fontWeight: 'medium'
                      }}
                    >
                      {Math.round(competitor.validity_score * 100)}
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Visit website" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleURLClick(competitor.url);
                    }}
                    aria-label="visit"
                    color="primary"
                  >
                    <ExportOutlined />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Typography variant="body2" color="text.primary">
              {competitor.description}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const CompetitorsLPReport = ({ reportData, processSubmit }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down('lg'));
  const drawerOpen = useSelector((state) => state.menu.drawerOpen);
  const userPlan = useSelector((state) => state.proAccountReducer.plan);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAnalysisUrl, setPendingAnalysisUrl] = useState(null);
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const isCompareModalOpen = searchParams.get('showCompareModal') === 'true';
  const [isCompareLoading, setIsCompareLoading] = useState(false);

  useEffect(() => {
    // Reset selection when report data changes
    setSelectedCompetitors([]);
  }, [reportData]);

  if (!reportData || !reportData.result) {
    return null;
  }

  const { result: competitorResults, task_id: taskId, url: mySiteUrl } = reportData;

  const handleSelectCompetitor = (competitorToToggle) => {
    setSelectedCompetitors((prevSelected) => {
      const isAlreadySelected = prevSelected.some((c) => c.url === competitorToToggle.url);
      if (isAlreadySelected) {
        return prevSelected.filter((c) => c.url !== competitorToToggle.url);
      } else {
        return [...prevSelected, competitorToToggle];
      }
    });
  };

  const handleAnalyze = (url) => {
    setPendingAnalysisUrl(url);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAnalyze = () => {
    if (pendingAnalysisUrl) {
      processSubmit({ analysisUrl: pendingAnalysisUrl });
    }
    setConfirmDialogOpen(false);
    setPendingAnalysisUrl(null);
  };

  const handleCloseDialog = () => {
    setConfirmDialogOpen(false);
    setPendingAnalysisUrl(null);
  };

  const handleNewComparison = () => {
    // Track the event with Yandex Metrika
    if (window.ym) {
      if (userPlan === 'Free') {
        window.ym(94496474, 'reachGoal', 'COMPARE_TO_COMPETITORS_FREE');
      } else {
        window.ym(94496474, 'reachGoal', 'COMPARE_TO_COMPETITORS');
      }
    }

    // Show upsell modal ONLY for users on the 'Free' plan.
    // Users on 'Trial' or any paid plan get direct access.
    if (userPlan === 'Free') {
      console.log('CompetitorsLPReport - opening compare modal (Free plan)');
      setIsCompareLoading(true);
      setSearchParams({ showCompareModal: 'true' });
      return;
    }

    // Set up the competitor step state (like CompetitorsStep.js does)
    // Pass all competitors with include flag indicating which ones are currently selected
    const allCompetitorsWithSelection = allCompetitors.map((competitor) => ({
      ...competitor,
      include: selectedCompetitors.some((selected) => selected.url === competitor.url)
    }));

    const competitorsData = {
      final_answer: {
        result: {
          results: allCompetitorsWithSelection.filter((c) => !c.isEmerging),
          emerging: allCompetitorsWithSelection.filter((c) => c.isEmerging)
        },
        task_id: taskId
      }
    };
    dispatch(updateAllCompetitors(competitorsData));
    dispatch(setCurrentURL({ currentURL: mySiteUrl }));
    dispatch(restoreDefaultParameters()); // Ensure default parameters are available

    // Format the URL as expected by the backend
    const formattedMySite = { currentURL: mySiteUrl };

    // Update the currentURL reducer with the correct format
    dispatch(setCurrentURL(formattedMySite));

    navigate('/competitors-history', { state: { showNewFlow: true } });
  };

  const handleCloseCompareModal = () => {
    console.log('CompetitorsLPReport - closing compare modal');
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('showCompareModal');
    setSearchParams(newSearchParams, { replace: true });
    setIsCompareLoading(false);
  };

  const allCompetitors = [
    ...(competitorResults.results?.map((c) => ({ ...c, isEmerging: false })) || []),
    ...(competitorResults.emerging?.map((c) => ({ ...c, isEmerging: true })) || [])
  ];

  return (
    <>
      <Box sx={{ width: '100%', mt: 4, pb: selectedCompetitors.length > 0 ? '100px' : '0' }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Competitors
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Select one or more competitors to start a comparison.
        </Typography>
        <Grid container spacing={2}>
          {allCompetitors.map((competitor) => {
            const isSelected = selectedCompetitors.some((c) => c.url === competitor.url);
            const competitorId = `competitor-card-${competitor.url.replace(/[^a-zA-Z0-9]/g, '-')}`;
            return (
              <Grid item xs={12} md={6} key={competitor.url} id={competitorId}>
                <CompetitorCard
                  competitor={competitor}
                  onAnalyze={handleAnalyze}
                  onSelect={handleSelectCompetitor}
                  isSelected={isSelected}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Contextual Action Bar */}
      {selectedCompetitors.length > 0 && (
        <AppBar
          position="fixed"
          color="default"
          sx={{
            top: 'auto',
            bottom: 0,
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(8px)',
            width: `calc(100% - ${drawerOpen && !matchDownLG ? `${config.drawerWidth}px` : '0px'})`
          }}
        >
          <Toolbar>
            <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {selectedCompetitors.length} competitor{selectedCompetitors.length > 1 ? 's' : ''} selected
              </Typography>
              <Button
                variant="contained"
                startIcon={isCompareLoading ? <CircularProgress size={20} color="inherit" /> : <SwapOutlined />}
                onClick={handleNewComparison}
                disabled={isCompareLoading}
                sx={{
                  height: 48,
                  px: 3
                }}
              >
                Compare to {selectedCompetitors.length} competitor{selectedCompetitors.length > 1 ? 's' : ''}
              </Button>
            </Container>
          </Toolbar>
        </AppBar>
      )}

      <CompetitorCompareModal open={isCompareModalOpen} handleClose={handleCloseCompareModal} />

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 400,
            width: '90vw',
            mx: 'auto',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            overflow: 'hidden'
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            margin: 2
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 3,
            px: 3,
            textAlign: 'center',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          Start New Analysis?
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <DialogContentText
            sx={{
              textAlign: 'center',
              fontSize: '0.95rem',
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
              mb: 1
            }}
          >
            This will start a new analysis for the selected competitor. Your current analysis report can be accessed later from the History.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            gap: 1.5,
            justifyContent: 'center'
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.divider
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAnalyze}
            variant="contained"
            autoFocus
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompetitorsLPReport;
